import { useEffect, useId, useRef } from "react";
import * as d3 from "d3";
import { apiFetch } from "../../api/apiClient";

/* =========================
   CONSTANTS (SAFE FOR HOOKS)
   ========================= */
const HEIGHT = 520;

const margin = {
  top: 50,
  right: 40,
  bottom: 80,
  left: 70,
};

const BINS = [
  [981, 1030], [932, 981], [1030, 1079], [883, 932], [834, 883],
  [785, 834], [736, 785], [687, 736], [491, 540], [589, 638],
  [638, 687], [540, 589], [442, 491], [393, 442], [344, 393],
  [295, 344], [1079, 1128], [1, 50], [50, 99], [99, 148],
  [148, 197], [197, 246], [246, 295],
];

/** Nominal bin width from the workbook edges (e.g. 981–1030). */
const BIN_WIDTH = 49;

/**
 * Extend the fixed workbook bins so every observation falls in a bin.
 * High V DC (often 1,200–1,370) used to sit above 1128 and never appear,
 * so cumulative % stopped around ~94–96%.
 */
function binsCoveringValues(values) {
  const bins = [...BINS]
    .map(([min, max]) => [min, max])
    .sort((a, b) => a[0] - b[0]);

  if (!values.length) {
    return bins;
  }

  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);

  while (bins[0][0] > minVal && bins[0][0] > 0) {
    const high = bins[0][0];
    const low = Math.max(0, high - BIN_WIDTH);
    if (low >= high) break;
    bins.unshift([low, high]);
    if (low === 0) break;
  }

  // Keep extending until the last half-open upper edge is strictly above maxVal.
  while (bins[bins.length - 1][1] <= maxVal) {
    const low = bins[bins.length - 1][1];
    bins.push([low, low + BIN_WIDTH]);
  }

  return bins;
}

function buildFrequencyDistribution(values) {
  const finite = values.filter(Number.isFinite);
  const bins = binsCoveringValues(finite);
  const observedMax = finite.length ? Math.max(...finite) : 0;

  const rows = bins.map(([min, max], index) => {
    const isLast = index === bins.length - 1;
    // Last bin is open-ended so nothing above the workbook max is dropped.
    const count = finite.filter((v) => (
      isLast ? v >= min : (v >= min && v < max)
    )).length;
    const label = isLast && observedMax >= max
      ? `(≥${min})`
      : `(${min}, ${max})`;
    return { label, count };
  });

  // Denominator = sum of displayed bins (every observation lands in one).
  const total = rows.reduce((sum, row) => sum + row.count, 0) || 1;

  let cumulative = 0;
  return rows
    .sort((a, b) => b.count - a.count)
    .map((d, index, arr) => {
      cumulative += d.count;
      // Force last point to 100% (avoids float drift / missed overflow).
      const cumulativePct = index === arr.length - 1
        ? 1
        : cumulative / total;
      return {
        label: d.label,
        count: d.count,
        cumulativePct,
      };
    });
}

export default function FrequencyDistribution({
  portfolioName = "",
  duration = "month",
  startDate = "",
  endDate = "",
  powerblockId = "",
  data = [],
  onTooltip,
}) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const dataRef = useRef(null);
  const onTooltipRef = useRef(onTooltip);
  onTooltipRef.current = onTooltip;

  // Keyboard navigation: which bin is selected and whether arrow-key driven
  // navigation is currently active. Refs (not state) so the d3 handlers can
  // read/write without forcing React re-renders or redraws.
  const keyboardIndexRef = useRef(0);
  const keyboardModeRef = useRef(false);

  const reactId = useId();
  const gradientId = `barGradient-${reactId.replace(/:/g, "")}`;

  useEffect(() => {
    if (!containerRef.current) return;

    // Tooltip div fallback (only when page-level ChartTooltip isn't used)
    const tooltip = onTooltipRef.current
      ? null
      : d3
          .select("body")
          .append("div")
          .style("position", "absolute")
          .style("left", "0")
          .style("top", "0")
          .style("background", "white")
          .style("border", "1px solid #ccc")
          .style("padding", "6px")
          .style("border-radius", "4px")
          .style("pointer-events", "none")
          .style("box-shadow", "0 0 10px rgba(0,0,0,0.25)")
          .style("opacity", 0);

    // Mutable handle to the latest draw cycle's selection helpers. Populated
    // at the end of every `draw()` call so the effect-scoped keyboard/focus
    // listeners (which outlive a single draw) can drive the most recent
    // chart instance.
    const handlersRef = { current: null };

    /* =========================
       DRAW FUNCTION (LAYOUT ONLY)
       ========================= */
    function draw() {
      if (!containerRef.current || !dataRef.current) return;

      const width = containerRef.current.getBoundingClientRect().width;

      const innerWidth =
        width - margin.left - margin.right;
      const innerHeight =
        HEIGHT - margin.top - margin.bottom;

      const svg = d3
        .select(svgRef.current)
        .attr("width", width)
        .attr("height", HEIGHT);

      svg.selectAll("*").remove();

      const defs = svg.append("defs");
      const grad = defs
        .append("linearGradient")
        .attr("id", gradientId)
        .attr("x1", "0")
        .attr("y1", "0")
        .attr("x2", "0")
        .attr("y2", "1");
      grad.append("stop").attr("offset", "0%").attr("stop-color", "#3b82f6");  // blue
      grad.append("stop").attr("offset", "100%").attr("stop-color", "#000");    // black

      const g = svg
        .append("g")
        .attr(
          "transform",
          `translate(${margin.left},${margin.top})`
        );

      const x = d3
        .scaleBand()
        .domain(dataRef.current.map(d => d.label))
        .range([0, innerWidth])
        .padding(0.15);

      const yLeft = d3
        .scaleLinear()
        .domain([0, Math.max(900, (d3.max(dataRef.current, (d) => d.count) ?? 0) * 1.1)])
        .nice()
        .range([innerHeight, 0]);

      const yRight = d3
        .scaleLinear()
        .domain([0, 1])
        .range([innerHeight, 0]);

      /* =========================
         TITLES & LABELS
         ========================= */
      svg.append("text")
        .attr("x", (width / 2) + 10)
        .attr("y", 30)
        .attr("text-anchor", "middle")
        .style("font-size", "20px")
        .style("fill", "#555")
        .style("font-weight", "bold")
        .text("Frequency Distribution");

      /* -------- Y AXES LABEL -------- */
      
        g.append("text")
        .attr("transform", "rotate(-90)")
        .attr("x", -innerHeight / 2)
        .attr("y", -margin.left + 20)
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "middle")
        .style("font-size", "14px")
        .style("font-weight", "bold")
        .text("Number of Observations");

      /* -------- AXES -------- */
      g.append("g").call(d3.axisLeft(yLeft).ticks(9));

      g.append("g")
        .attr("transform", `translate(${innerWidth},0)`)
        .call(
          d3.axisRight(yRight).tickFormat(d3.format(".0%"))
        );

      g.append("g")
        .attr("transform", `translate(0,${innerHeight})`)
        .call(d3.axisBottom(x))
        .selectAll("text")
        .attr("transform", "rotate(90)")
        .style("text-anchor", "start")
        .attr("dx", "1em")
        .attr("dy", "-0.5em");

      /* -------- BARS -------- */
      const BAR_COLOR_ACTIVE = "#3b82f6";
      const barFill = `url(#${gradientId})`;
      const bars = g.selectAll("rect")
        .data(dataRef.current)
        .enter()
        .append("rect")
        .attr("x", d => x(d.label))
        .attr("y", d => yLeft(d.count))
        .attr("width", x.bandwidth())
        .attr("height", d => innerHeight - yLeft(d.count))
        .attr("fill", barFill)
        .style("pointer-events", "none");

      /* -------- LINE -------- */
      g.append("path")
        .datum(dataRef.current)
        .attr("fill", "none")
        .attr("stroke", "#ff9000")
        .attr("stroke-width", 3)
        .attr(
          "d",
          d3.line()
            .x(d => x(d.label) + x.bandwidth() / 2)
            .y(d => yRight(d.cumulativePct))
        );

      /* -------- LINE DOTS (small by default; enlarged at active bin) -------- */
      const DOT_R = 3;
      const DOT_R_ACTIVE = 7;

      const lineDots = g.selectAll(".line-dot")
        .data(dataRef.current)
        .enter()
        .append("circle")
        .attr("class", "line-dot")
        .attr("cx", d => x(d.label) + x.bandwidth() / 2)
        .attr("cy", d => yRight(d.cumulativePct))
        .attr("r", DOT_R)
        .attr("fill", "#ff9000")
        .style("pointer-events", "none");

      /* =========================
         CROSSHAIR + TOOLTIP
         Snap-by-x to nearest bin; tooltip anchors to the snapped bar's
         top edge (centered on the bar) rather than following the cursor,
         matching SingleBarChart's stable hover behavior.
         ========================= */
      const gCrosshair = g.append("g").attr("class", "crosshair");
      const gOverlay = g.append("g").attr("class", "overlay");

      const crosshair = gCrosshair.append("line")
        .attr("stroke", "rgba(0,0,0,0.45)")
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "2,3")
        .attr("y1", 0)
        .attr("y2", innerHeight)
        .style("opacity", 0)
        .style("pointer-events", "none");

      const centers = dataRef.current.map(d => x(d.label) + x.bandwidth() / 2);

      const resetDots = () => {
        lineDots.attr("r", DOT_R);
      };

      const resetBars = () => {
        bars.attr("fill", barFill);
      };

      // Anchor the tooltip to the snapped bar (centered on the bar, just
      // above its top) instead of the cursor. The fallback <div> tooltip
      // uses page coordinates; the page-level callback uses viewport
      // coordinates so it can be positioned by a fixed-position element.
      const showTooltipForDatum = (d, centerX) => {
        const barTopY = yLeft(d.count);
        const svgEl = svgRef.current;
        if (!svgEl) return;
        const rect = svgEl.getBoundingClientRect();

        const tip = onTooltipRef.current;
        if (tip) {
          tip({
            visible: true,
            x: rect.left + margin.left + centerX,
            y: rect.top + margin.top + barTopY - 12,
            content: (
              <>
                <strong>RANGE:</strong> {d.label}
                <br />
                <strong>COUNT:</strong> {d.count}
                <br />
                <strong>PERCENTAGE:</strong> {(d.cumulativePct * 100).toFixed(2)}%
              </>
            ),
          });
        } else if (tooltip) {
          tooltip
            .style("opacity", 1)
            .html(
              `<strong>RANGE:</strong> ${d.label}<br/>` +
              `<strong>COUNT:</strong> ${d.count}<br/>` +
              `<strong>PERCENTAGE:</strong> ${(d.cumulativePct * 100).toFixed(2)}%`
            )
            .style(
              "left",
              `${rect.left + window.scrollX + margin.left + centerX + 10}px`
            )
            .style(
              "top",
              `${rect.top + window.scrollY + margin.top + barTopY - 30}px`
            );
        }
      };

      const showSelectionByIndex = (idx) => {
        if (!centers.length) return;
        const clamped = Math.max(0, Math.min(centers.length - 1, idx));
        keyboardIndexRef.current = clamped;
        const d = dataRef.current[clamped];
        if (!d) return;

        crosshair
          .attr("x1", centers[clamped])
          .attr("x2", centers[clamped])
          .style("opacity", 1);

        resetDots();
        lineDots.filter((_, i) => i === clamped).attr("r", DOT_R_ACTIVE);

        resetBars();
        bars.filter((_, i) => i === clamped).attr("fill", BAR_COLOR_ACTIVE);

        showTooltipForDatum(d, centers[clamped]);
      };

      const hideSelection = () => {
        crosshair.style("opacity", 0);
        resetDots();
        resetBars();
        const tip = onTooltipRef.current;
        if (tip) {
          tip((t) => ({ ...t, visible: false }));
        } else {
          tooltip?.style("opacity", 0);
        }
      };

      gOverlay
        .append("rect")
        .attr("width", innerWidth)
        .attr("height", innerHeight)
        .attr("fill", "transparent")
        .style("cursor", "crosshair")
        .on("mousemove", (event) => {
          if (!centers.length) return;
          // Any real mouse movement on the chart kicks us out of keyboard
          // navigation mode and back into mouse-follow mode.
          keyboardModeRef.current = false;
          const [mx] = d3.pointer(event);
          const idx = Math.max(
            0,
            Math.min(centers.length - 1, d3.bisectCenter(centers, mx))
          );
          showSelectionByIndex(idx);
        })
        .on("mouseleave", () => {
          // If the chart still has keyboard focus when the mouse leaves,
          // fall back to keyboard-navigation mode so the user can keep
          // arrow-keying without losing the visible selection.
          if (svgRef.current && document.activeElement === svgRef.current) {
            keyboardModeRef.current = true;
            showSelectionByIndex(keyboardIndexRef.current);
          } else {
            hideSelection();
          }
        });

      // Publish this draw cycle's selection helpers so the effect-scoped
      // keyboard/focus listeners (which outlive a single draw) can drive
      // the most recent chart instance.
      handlersRef.current = {
        centersLength: centers.length,
        showSelectionByIndex,
        hideSelection,
      };

      // If the chart was already focused when this draw ran (e.g. resize
      // fired while the user was navigating with the keyboard), re-render
      // the selection so the crosshair persists across redraws.
      if (
        keyboardModeRef.current &&
        svgRef.current &&
        document.activeElement === svgRef.current
      ) {
        showSelectionByIndex(keyboardIndexRef.current);
      }
    }

    /* =========================
       LOAD DATA ONCE
       ========================= */
    let cancelled = false;

    const normalizeRows = (payload) => {
      if (Array.isArray(payload)) return payload;
      if (Array.isArray(payload?.results)) return payload.results;
      if (typeof payload === "string") {
        try {
          const parsed = JSON.parse(payload);
          return Array.isArray(parsed) ? parsed : (Array.isArray(parsed?.results) ? parsed.results : []);
        } catch {
          return [];
        }
      }
      return [];
    };

    const extractNumericValue = (row) => {
      if (typeof row === "number") return row;
      if (row && typeof row === "object") {
        const numeric = Object.values(row).find((value) => Number.isFinite(Number(value)));
        return numeric !== undefined ? Number(numeric) : NaN;
      }
      return NaN;
    };

    const hasProvidedData = Array.isArray(data) && data.length > 0;

    const buildDistribution = (rows) => {
      const values = rows.map(extractNumericValue).filter(Number.isFinite);
      return buildFrequencyDistribution(values);
    };

    const load = async () => {
      if (hasProvidedData) {
        const rows = normalizeRows(data);
        dataRef.current = buildDistribution(rows);
        draw();
        return;
      }
      if (!portfolioName || !powerblockId) return;
      try {
        const query = new URLSearchParams({
          device: "powerblock",
          powerblockId: String(powerblockId),
        });
        if (startDate && endDate) {
          query.set("start", startDate);
          query.set("end", endDate);
        }
        const payload = await apiFetch(`/api/graph/portfolio/${portfolioName}/insights/${encodeURIComponent(duration)}/frequency-distribution?${query}`);
        if (cancelled) return;

        const rows = normalizeRows(payload);
        dataRef.current = buildDistribution(rows);

        draw();
      } catch (err) {
        console.warn("Frequency distribution API fetch failed:", err);
      }
    };

    load();

    /* =========================
       RESIZE HANDLING (LAYOUT ONLY)
       ========================= */
    const observer = new ResizeObserver(draw);
    observer.observe(containerRef.current);

    /* =========================
       KEYBOARD NAVIGATION
       Arrow-key step through bins while the <svg> has focus. Listeners are
       attached once per effect run and read from `handlersRef` so they
       always drive the most recent draw cycle.
       ========================= */
    const svgEl = svgRef.current;

    const handleKeyDown = (e) => {
      const h = handlersRef.current;
      if (!keyboardModeRef.current || !h || !h.centersLength) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (keyboardIndexRef.current > 0) {
          h.showSelectionByIndex(keyboardIndexRef.current - 1);
        }
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (keyboardIndexRef.current < h.centersLength - 1) {
          h.showSelectionByIndex(keyboardIndexRef.current + 1);
        }
      }
    };

    const handleFocus = () => {
      const h = handlersRef.current;
      if (!h || !h.centersLength) return;
      keyboardModeRef.current = true;
      h.showSelectionByIndex(keyboardIndexRef.current);
    };

    const handleBlur = () => {
      keyboardModeRef.current = false;
      handlersRef.current?.hideSelection();
    };

    svgEl?.addEventListener("keydown", handleKeyDown);
    svgEl?.addEventListener("focus", handleFocus);
    svgEl?.addEventListener("blur", handleBlur);

    return () => {
      cancelled = true;
      observer.disconnect();
      tooltip?.remove?.();
      svgEl?.removeEventListener("keydown", handleKeyDown);
      svgEl?.removeEventListener("focus", handleFocus);
      svgEl?.removeEventListener("blur", handleBlur);
    };
  }, [portfolioName, duration, startDate, endDate, powerblockId, data, gradientId]);

  return (
    <div ref={containerRef} style={{ width: "100%", minWidth: "0" }}>
      <svg
        ref={svgRef}
        tabIndex={0}
        role="img"
        aria-label="Frequency distribution chart. Use left and right arrow keys to navigate between bins."
        style={{ display: "block" }}
      />
    </div>
  );
}